// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Badge } from '@loa/ui-components';

const RISK_COLOR: Record<number, 'green' | 'blue' | 'yellow' | 'red'> = {
  1: 'green',
  2: 'green',
  3: 'blue',
  4: 'blue',
  5: 'yellow',
  6: 'red',
  7: 'red',
};

export interface RiskRatingChipProps {
  /** Canonical 1-7 rating, or null when the provider published none. */
  rating: number | null | undefined;
  /** Verbatim provider value, shown in the title tooltip when present. */
  raw?: string | null;
  size?: 'sm' | 'md';
}

/**
 * Renders a fund's canonical risk rating (1-7) as a colour-banded chip —
 * green (low) → blue → yellow → red (high). Falls back to an em-dash when the
 * provider didn't publish a rating (uneven coverage is expected).
 */
export function RiskRatingChip({ rating, raw, size = 'sm' }: RiskRatingChipProps) {
  if (rating == null) {
    return <span className="text-grey-03" aria-label="No risk rating">—</span>;
  }
  const title = raw ? `Risk ${rating} of 7 (provider: ${raw})` : `Risk ${rating} of 7`;
  return (
    <span title={title}>
      <Badge color={RISK_COLOR[rating] ?? 'grey'} size={size}>
        {rating}/7
      </Badge>
    </span>
  );
}
